import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { UnreadCountFn } from '@/features/messages'
import type { SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import type { SnackbarRetryWrapperFactory } from '@/features/shell'
import type { PromptArchiveJobFn } from '@/features/jobs'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { ViewModelList } from '@/infra/view-model-list'
import type { SearchableJob } from '@/features/jobs/list/SearchableJob'
import { JobListItemViewModel } from '@/features/jobs/list/JobListItemViewModel'
import { computed } from 'mobx'
import type { Observable } from 'rxjs'
import type { Id } from '@/infra/id'
import { tapAction } from '@/utils/mobx-util'

export interface JobListViewModelOptions {
  /**
   * How many items to load.
   */
  limit: number

  /**
   * If specified, allows pre-processing the list of models.
   *
   * @param jobs
   */
  models?: (jobs: readonly SearchableJob[]) => readonly SearchableJob[]
}

/**
 * Options for {@link JobListViewModel.fetch}
 */
export interface JobListFetchOptions {
  filter?: string
  orderBy?: string
  staleWhileRevalidate?: boolean
  signal?: AbortSignal
}

/**
 * List of job view models backed by job search.
 */
export class JobListViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _jobVms
  private _filter = ''
  private _orderBy = ''

  /**
   * The infinite scroll provider.
   */
  readonly scroller

  constructor(
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _unreadCount: UnreadCountFn,
    private readonly _searchStore: SearchableJobStore,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    private readonly _promptArchiveJob: PromptArchiveJobFn,
    jobArchived$: Observable<Id>,
    private readonly _options: JobListViewModelOptions,
  ) {
    // Remove jobs from the scroller that get archived.
    this._lifecycle.manage(
      jobArchived$.pipe(
        tapAction((id) => this.scroller.removeWhere((j) => j.id === id)),
      ),
    )
    this._lifecycle.manage(_searchStore.refreshSearchableJobs$)

    this._jobVms = new ViewModelList({
      create: (job) => this._createJobListItemViewModel(job),
      activate: (vm) => vm.activate(),
      deactivate: (vm) => vm.deactivate(),
      models: () => {
        if (this._options.models) {
          const processed = this._options.models(this.scroller.items)
          if (processed === this.scroller.items) {
            // Make sure we access the elements if the models function returned
            // the exact input array.
            return processed.slice()
          }

          return processed
        }

        return this.scroller.items.slice()
      },
    })
    this._lifecycle.manage(this._jobVms)

    this.scroller = new InfiniteScrollProvider({
      limit: this._options.limit,
      fetchItems: ({ paging, signal }) => {
        const withRetry = this._createSnackbarRetryWrapper(
          'Loading jobs failed',
          signal,
        )

        const member = this._getCurrentMember()
        const defaultFilter = [
          `workspace_id="${member.workspaceId}"`,
          `clients.user_public_id:"${member.user.id}"`,
        ].join(' AND ')

        const filter = this._filter
          ? [defaultFilter, `(${this._filter})`].join(' AND ')
          : defaultFilter

        return withRetry(() =>
          this._searchStore.list({
            filter,
            limit: paging.limit,
            after: paging.after,
            orderBy: this._orderBy,
          }),
        )
      },
    })
  }

  /**
   * Total count as reported by the server.
   */
  @computed get total() {
    return this.scroller.total
  }

  /**
   * The job view models.
   */
  @computed get jobVms() {
    return this._jobVms.items
  }

  /**
   * Whether we're fetching the initial items.
   */
  @computed get isFetchingInitial() {
    return this.scroller.fetch.pending && !this.scroller.isRevalidating
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches jobs from the beginning.
   */
  async fetch({
    filter,
    orderBy,
    staleWhileRevalidate,
    signal,
  }: JobListFetchOptions) {
    this._filter = filter ?? ''
    this._orderBy = orderBy ?? ''
    await this.scroller.fetch({ signal, staleWhileRevalidate })
  }

  /**
   * Creates a new job list item view model for a job list.
   *
   * @param job
   */
  private _createJobListItemViewModel(job: SearchableJob) {
    return new JobListItemViewModel(
      job,
      this._unreadCount,
      this._promptArchiveJob,
    )
  }
}

/**
 * Factory for creating a job list view model.
 */
export type JobListViewModelFactory = ReturnType<
  typeof createJobListViewModelFactory
>

/** @see JobListViewModelFactory */
export function createJobListViewModelFactory(
  getCurrentMember: GetCurrentMemberFn,
  unreadCount: UnreadCountFn,
  searchStore: SearchableJobStore,
  createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
  promptArchiveJob: PromptArchiveJobFn,
  jobArchived$: Observable<Id>,
) {
  return (options: JobListViewModelOptions) =>
    new JobListViewModel(
      getCurrentMember,
      unreadCount,
      searchStore,
      createSnackbarRetryWrapper,
      promptArchiveJob,
      jobArchived$,
      options,
    )
}
